<template>
  <div
    class="pb-panel"
    :class="classes"
  >
    <div
      v-if="hasHeaderSlot"
      class="pb-panel-header"
      @mousedown="deselect"
    >
      <slot name="header" />
    </div>
    <div
      v-if="hasDefaultSlot"
      class="pb-panel-content"
      @mousedown="deselect"
    >
      <slot />
    </div>
    <div
      v-if="hasFooterSlot"
      class="pb-panel-footer"
      @mousedown="deselect"
    >
      <slot name="footer" />
    </div>
    <div
      v-if="props.loading"
      class="pb-panel-loader"
    >
      <Spinner />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import Spinner from '@nicolaudie-group/ng-ui-kit/components/Spinner/Spinner.vue'
import useHasContent from '@nicolaudie-group/ng-ui-kit/composables/useHasContent'
import useReducedState from '@/composables/useReducedState'

const props = defineProps({
  loading: { type: Boolean, default: false },
  class: { type: [Object, String, Array], default: '' },
})

const emit = defineEmits(['deselect'])

const dropdownActive = useReducedState(undefined, 'dropdowns')

function deselect(event) {
  /*
  We first check that the mousedown event wasn't in an interactive element.
  We basically we check that we clicked on the background of the panel.
  We also check that there was no dropdown active, because in this case the dropdown
  should close, and only after all dropdowns are closed we can deselect elements.
  */
  if (!event.target.closest('a, button, input, label, textarea, .linkbox-wrapper') && !dropdownActive.value) {
    /*
    We first blur the active element.
    We do that because mousedown event is triggered before the blur of the current active element.
    We do need mousedown, because:
    - mouseup is triggered even if the click started outside of the panel
    - depending on browser, click is triggered also when the click finishes outside the panel

    We also need the blur to happen before emitting the "deselect" event, because otherwise
    the "deselect" event can cause a component that contains a focused input to be unmounted before
    it's "@change" handler is called.
    */
    document.activeElement?.blur()
    emit('deselect', event)
  }
}

const classes = computed(() => {
  const result = {}
  if (Array.isArray(props.class)) {
    for (let i = 0; i < props.class.length; i += 1) {
      result[props.class[i]] = true
    }
  } else if (typeof props.class === 'string') {
    result[props.class] = true
  } else {
    Object.assign(result, props.class)
  }
  result.loading = props.loading
  return result
})

const hasHeaderSlot = useHasContent('header')
const hasDefaultSlot = useHasContent('default')
const hasFooterSlot = useHasContent('footer')
</script>

<style lang="scss" src="./EditorPanel.scss" />
